import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import ReactSelect from 'react-select'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'

import {
  reportingCompanySelected,
  reportDateChanged,
  handleAddReconUsers,
  handleSyncCompany,
} from '../../actions/backup365ReportsActions'

import { Container, Row, Col, Card, CardBody } from 'reactstrap'
import UsersTable from '../Backup365UsersTable'
import Backup365SummaryCard from '../Backup365SummaryCard'
import BackupReconciliationCard from '../Backup365ReconCard'

const CompanyReportsTab = () => {
  const dispatch = useDispatch()

  const companyLabels = useSelector((state) => state.backup365Reports.companyLabels)
  const selectedCompany = useSelector((state) => state.backup365Reports.selectedCompany)
  const reportDates = useSelector((state) => state.backup365Reports.reportDates)
  const selectedReportDate = useSelector((state) => state.backup365Reports.selectedReportDate)
  const reportDetails = useSelector((state) => state.backup365Reports.reportDetails)
  const pendingCompanyRecon = useSelector((state) => state.backup365Reports.pendingCompanyRecon)

  const [confirmSync, setConfirmSync] = React.useState(false)
  const [syncCooldown, setSyncCooldown] = React.useState(false)

  return (
    <>
      <Card className="shadow-sm border-0 mb-4">
        <CardBody>
          <Row className="gy-3">
            {/* Company Selector */}
            <Col xs="12" md="4">
              <label className="form-label fw-semibold">Select a Client</label>
              <ReactSelect
                value={selectedCompany}
                onChange={(selectedOption) => dispatch(reportingCompanySelected(selectedOption))}
                isClearable
                isSearchable
                options={companyLabels}
                placeholder="Choose a company..."
              />
            </Col>
            {/* Report Date Selector */}
            {reportDates && reportDates.length > 0 && (
              <Col xs="12" md="4">
                <label className="form-label fw-semibold">Report Date</label>
                <DatePicker
                  selected={selectedReportDate ? new Date(selectedReportDate.value ?? selectedReportDate) : null}
                  onChange={(date) => {
                    if (!date) {
                      dispatch(reportDateChanged(null))
                      return
                    }

                    dispatch(
                      reportDateChanged({
                        value: date.toISOString(),
                        label: date.toLocaleDateString('en-AU', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                        }),
                      })
                    )
                  }}
                  includeDates={reportDates.map((d) => new Date(d))}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Select a report date..."
                  className="form-control"
                  isClearable
                />
              </Col>
            )}
            {selectedCompany && (
              <Col xs="12" md="2" className="d-flex align-items-end">
                <button
                  className="btn btn-outline-primary w-100"
                  onClick={() => {
                    dispatch(reportingCompanySelected(selectedCompany))
                  }}
                >
                  Refresh
                </button>
              </Col>
            )}
            {selectedCompany && (
              <Col xs="12" md="2" className="d-flex align-items-end">
                {!confirmSync ? (
                  <button
                    className="btn btn-primary w-100"
                    disabled={syncCooldown}
                    onClick={() => setConfirmSync(true)}
                  >
                    {syncCooldown ? 'Please wait...' : 'Sync Now'}
                  </button>
                ) : (
                  <div className="d-flex w-100 gap-2">
                    <button
                      className="btn btn-success w-50 mr-2"
                      onClick={() => {
                        // Run the sync
                        dispatch(handleSyncCompany(selectedCompany.value))

                        // Close confirmation
                        setConfirmSync(false)

                        // Start cooldown timer
                        setSyncCooldown(true)
                        setTimeout(() => setSyncCooldown(false), 5000)
                      }}
                    >
                      ✓
                    </button>

                    <button className="btn btn-danger w-50" onClick={() => setConfirmSync(false)}>
                      ✕
                    </button>
                  </div>
                )}
              </Col>
            )}
          </Row>
        </CardBody>
      </Card>

      {/* Summary Cards */}
      {reportDetails && (
        <>
          <LicenseChangeCards reportDetails={reportDetails} />
          <Backup365SummaryCard reportDetails={reportDetails} />
          <BackupReconciliationCard
            reconciliation={pendingCompanyRecon}
            onAddUsersClick={(productId) => dispatch(handleAddReconUsers(pendingCompanyRecon.id, productId))}
          />
        </>
      )}

      {/* Users Table */}
      {reportDetails && reportDetails.users?.length > 0 ? (
        <Card className="shadow-sm border-0">
          <CardBody>
            <UsersTable users={reportDetails.users} selectedCompany={selectedCompany} />
          </CardBody>
        </Card>
      ) : selectedCompany ? (
        <Card className="shadow-sm border-0">
          <CardBody>
            <p className="text-muted m-0">No report data found for the selected company or date.</p>
          </CardBody>
        </Card>
      ) : (
        <div className="text-muted mt-4">Please select a company to view report details.</div>
      )}
    </>
  )
}

const LicenseChangeCards = ({ reportDetails }) => {
  return (
    <div className="backup365-summary-card mb-4">
      <div className="card shadow-sm border-0">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-start flex-wrap backup365-summary-row">
            <LicenseChangeItem
              label="Total Licenses"
              count={reportDetails.totalLicenseCount}
              delta={reportDetails.totalLicenseDelta}
              color="text-primary"
            />
            <LicenseChangeItem
              label="Complete Licenses"
              count={reportDetails.completeLicenseCount}
              delta={reportDetails.completeLicenseDelta}
              color="text-info"
            />
            <LicenseChangeItem
              label="Mailbox Licenses"
              count={reportDetails.mailboxLicenseCount}
              delta={reportDetails.mailboxLicenseDelta}
              color="text-success"
            />
          </div>
        </div>
      </div>
    </div>
  )
}

const LicenseChangeItem = ({ label, count, delta, color }) => {
  const isIncrease = delta > 0
  const isDecrease = delta < 0
  const arrow = isIncrease ? '▲' : isDecrease ? '▼' : '–'
  const deltaColor = isIncrease ? 'text-success' : isDecrease ? 'text-danger' : 'text-muted'

  return (
    <div className="summary-item text-center">
      <h6 className="text-muted mb-1">{label}</h6>
      <h4 className={`fw-bold ${color} mb-0`}>{count}</h4>
      <small className={deltaColor}>
        {arrow} {delta >= 0 ? `+${delta}` : delta}
      </small>
    </div>
  )
}

export default CompanyReportsTab
